/**
 * Babble planning (audio-direction §5: "syllable-like babble, no real words"): phrases of words of syllables
 * (CV, CVC, V, CCV), each syllable a consonant class (plosive, fricative, nasal, liquid) and a vowel from the
 * speaker's accent, with intonation (declination across a phrase, a stressed syllable per word, a final fall or
 * a question rise, vibrato and jitter applied at render). Stress raises the rate, the pitch, the pitch range
 * and the level. The output is a list of timed segments with formant, voicing, noise and pitch targets.
 * Pure and deterministic for (voice, line seed, stress).
 */
import { createRng, nextFloat, type Rng } from '../../core/rng'
import type { CommsConfig } from './commsSchema'
import type { Voice } from './voice'

export type SegmentKind = 'vowel' | 'closure' | 'burst' | 'fricative' | 'nasal' | 'liquid' | 'pause'

export interface Segment {
  kind: SegmentKind
  start: number
  dur: number
  /** Formant targets (Hz), scaled by the speaker's tract. */
  f1: number
  f2: number
  f3: number
  /** Voicing and noise amplitudes 0..1. */
  voice: number
  noise: number
  /** Noise band (bursts and fricatives). */
  noiseHz: number
  noiseQ: number
  /** Pitch at the segment start and end (Hz). */
  p0: number
  p1: number
}

export interface Utterance {
  readonly voice: Voice
  readonly stress: number
  readonly segments: readonly Segment[]
  readonly seconds: number
  /** Overall level 0..1 (stress makes people louder). */
  readonly level: number
}

function inRange(rng: Rng, r: readonly [number, number]): number {
  return r[0] + (r[1] - r[0]) * nextFloat(rng)
}

function intIn(rng: Rng, r: readonly [number, number]): number {
  return Math.floor(r[0] + (r[1] - r[0] + 1) * nextFloat(rng))
}

function pick<T>(rng: Rng, items: readonly T[], weight: (t: T) => number): T {
  let total = 0
  for (const it of items) total += weight(it)
  let u = nextFloat(rng) * total
  for (const it of items) {
    u -= weight(it)
    if (u <= 0) return it
  }
  return items[items.length - 1]
}

/** Mixes the character seed and the line seed. */
export function lineRngSeed(voiceSeed: number, lineSeed: number): number {
  let h = (Math.imul(voiceSeed, 0x9e3779b1) ^ Math.imul(lineSeed + 1, 0x85ebca77)) >>> 0
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0
  return (h ^ (h >>> 15)) >>> 0
}

export function planUtterance(voice: Voice, lineSeed: number, stress: number, cfg: CommsConfig): Utterance {
  const rng = createRng(lineRngSeed(voice.seed, lineSeed))
  const s = Math.min(1, Math.max(0, stress))
  const p = cfg.prosody
  const c = cfg.consonants
  const tract = voice.tract
  const speed = (voice.rate * (1 + s * cfg.stress.rate)) / 5
  const base = voice.pitch * (1 + s * cfg.stress.pitch)
  const range = voice.range * (1 + s * cfg.stress.range)
  const vowelNames = Object.keys(cfg.vowels)
  const segments: Segment[] = []
  let t = 0

  function push(kind: SegmentKind, dur: number, f: readonly [number, number, number], voiceAmp: number, noise: number, noiseHz: number, noiseQ: number, p0: number, p1: number): void {
    segments.push({ kind, start: t, dur, f1: f[0] * tract, f2: f[1] * tract, f3: f[2] * tract, voice: voiceAmp, noise, noiseHz, noiseQ, p0, p1 })
    t += dur
  }

  const phrases = intIn(rng, p.phrases)
  for (let ph = 0; ph < phrases; ph++) {
    const words = intIn(rng, p.wordsPerPhrase)
    const question = nextFloat(rng) < p.questionChance
    // Estimate the phrase's syllable count for the declination slope.
    const wordSyllables: number[] = []
    for (let w = 0; w < words; w++) wordSyllables.push(intIn(rng, p.syllablesPerWord))
    const total = wordSyllables.reduce((a, b) => a + b, 0)
    let k = 0
    for (let w = 0; w < words; w++) {
      const stressed = Math.floor(nextFloat(rng) * wordSyllables[w])
      for (let sy = 0; sy < wordSyllables[w]; sy++, k++) {
        const pos = total > 1 ? k / (total - 1) : 0
        const isStressed = sy === stressed
        const lift = range * (nextFloat(rng) - 0.5) * 0.6 + (isStressed ? p.stressPitch * (1 + s) : 0)
        const pitch = base * (1 + lift - p.declination * pos)
        // The phrase-final syllable falls (a statement) or rises (a question); others drift down with the phrase.
        const endPitch = k === total - 1 ? pitch * (question ? 1 + p.finalFall : 1 - p.finalFall) : pitch * (1 - p.declination / Math.max(total, 1))
        const vowel = cfg.vowels[pick(rng, vowelNames, (n) => voice.vowelWeights[vowelNames.indexOf(n)])]
        const shape = pick(rng, cfg.syllables.shapes, (x) => x.weight).shape
        const onsets = shape === 'V' ? 0 : shape === 'CCV' ? 2 : 1
        for (let o = 0; o < onsets; o++) consonant(pitch, vowel)
        const vd = (inRange(rng, cfg.syllables.vowel) / speed) * (isStressed ? p.stressLength : 1)
        push('vowel', vd, vowel, 1, voice.breath * 0.5, 0, 1, pitch, endPitch)
        if (shape === 'CVC') consonant(endPitch, vowel)
      }
      const gap = inRange(rng, p.wordGap) / speed
      if (w < words - 1 && gap > 0.012) push('pause', gap, vowel0(), 0, 0, 0, 1, base, base)
    }
    if (ph < phrases - 1) push('pause', inRange(rng, p.phraseGap), vowel0(), 0, 0, 0, 1, base, base)
  }

  function vowel0(): readonly [number, number, number] {
    return cfg.vowels[vowelNames[0]]
  }

  function consonant(pitch: number, nextVowel: readonly [number, number, number]): void {
    const cls = pick(rng, cfg.syllables.classes, (x) => x.weight).cls
    if (cls === 'plosive') {
      const pl = c.plosives[Math.floor(nextFloat(rng) * c.plosives.length)]
      push('closure', inRange(rng, c.closure) / speed, nextVowel, pl.voiced ? 0.25 : 0, 0, pl.hz, pl.q, pitch, pitch)
      push('burst', inRange(rng, c.burst), nextVowel, pl.voiced ? 0.45 : 0, 1, pl.hz, pl.q, pitch, pitch)
    } else if (cls === 'fricative') {
      const fr = c.fricatives[Math.floor(nextFloat(rng) * c.fricatives.length)]
      push('fricative', inRange(rng, c.fricative) / speed, nextVowel, fr.name === 'h' ? 0.1 : 0, fr.gain, fr.hz, fr.q, pitch, pitch)
    } else if (cls === 'nasal') {
      const n = c.nasals[Math.floor(nextFloat(rng) * c.nasals.length)]
      push('nasal', inRange(rng, c.sonorant) / speed, [n.f1, n.f2, n.f3], 0.55, 0, 0, 1, pitch, pitch)
    } else {
      const l = c.liquids[Math.floor(nextFloat(rng) * c.liquids.length)]
      push('liquid', inRange(rng, c.sonorant) / speed, [l.f1, l.f2, l.f3], 0.75, 0, 0, 1, pitch, pitch)
    }
  }

  return { voice, stress: s, segments, seconds: t, level: Math.min(1, 0.75 + s * cfg.stress.gain) }
}

/** Fraction of the utterance that is voiced (a sanity measure: babble must mostly sound like voice). */
export function voicedFraction(u: Utterance): number {
  let v = 0
  for (const seg of u.segments) if (seg.voice >= 0.5) v += seg.dur
  return u.seconds > 0 ? v / u.seconds : 0
}
